/**
 * Finds timeable durations ("Bake for 30 minutes", "simmer 10-12 min",
 * "rest 1 hour 15 minutes") in recipe step text so the UI can turn them into
 * tap-to-start timers. Parsing is purely client-side over free text, so it
 * favors precision: anything ambiguous is left as plain text.
 */

export type StepDuration = {
  /** Start offset of the matched phrase in the input text. */
  start: number;
  /** End offset (exclusive) of the matched phrase. */
  end: number;
  /** The matched phrase, verbatim. */
  text: string;
  /** Timer length in seconds. For ranges this is the lower bound, so the
   *  alarm fires before food overcooks rather than after. */
  seconds: number;
};

const UNICODE_FRACTIONS: Record<string, number> = {
  '½': 1 / 2, '¼': 1 / 4, '¾': 3 / 4, '⅓': 1 / 3, '⅔': 2 / 3,
  '⅛': 1 / 8, '⅜': 3 / 8, '⅝': 5 / 8, '⅞': 7 / 8,
};

const WORD_NUMBERS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, fifteen: 15,
  twenty: 20, thirty: 30, forty: 40, 'forty-five': 45, fifty: 50, sixty: 60,
  ninety: 90,
};

const FRAC = '[½¼¾⅓⅔⅛⅜⅝⅞]';
// Longest alternatives first so "1 1/2" isn't consumed as "1".
const NUM = [
  `\\d+\\s+\\d+\\/\\d+`,
  `\\d+\\s*${FRAC}`,
  `\\d+\\/\\d+`,
  `\\d+(?:\\.\\d+)?`,
  FRAC,
  `half(?:\\s+an?)?`,
  `forty-five`,
  ...Object.keys(WORD_NUMBERS).filter((w) => w !== 'forty-five'),
].join('|');
const UNIT = `hours?|hrs?|minutes?|mins?|seconds?|secs?`;
const RANGE_SEP = `\\s*(?:-|–|—|to|or)\\s*`;

// NUM [range NUM] UNIT — e.g. "30 minutes", "10-12 min", "1 ½ hours".
const DURATION_RE = new RegExp(
  `(${NUM})(?:${RANGE_SEP}(${NUM}))?[\\s-]*(${UNIT})\\b`,
  'gi',
);
// Optional smaller-unit tail: "1 hour[, and] 15 minutes".
const COMPOUND_RE = new RegExp(`,?\\s*(?:and\\s+)?(${NUM})[\\s-]*(${UNIT})\\b`, 'iy');
// Optional repeated-unit range tail: "10 minutes to 15 minutes".
const RANGE_TAIL_RE = new RegExp(`${RANGE_SEP}(?:${NUM})[\\s-]*(?:${UNIT})\\b`, 'iy');

const MIN_SECONDS = 5;
const MAX_SECONDS = 24 * 60 * 60;

const parseNumber = (raw: string): number | null => {
  const s = raw.trim().toLowerCase();
  if (s in WORD_NUMBERS) return WORD_NUMBERS[s];
  if (s.startsWith('half')) return 0.5;
  let m = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
  m = s.match(new RegExp(`^(\\d+)\\s*(${FRAC})$`));
  if (m) return Number(m[1]) + UNICODE_FRACTIONS[m[2]];
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return Number(m[2]) === 0 ? null : Number(m[1]) / Number(m[2]);
  if (s in UNICODE_FRACTIONS) return UNICODE_FRACTIONS[s];
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const unitSeconds = (unit: string): number => {
  const u = unit.toLowerCase();
  if (u.startsWith('h')) return 3600;
  if (u.startsWith('m')) return 60;
  return 1;
};

export const parseStepDurations = (text: string): StepDuration[] => {
  const results: StepDuration[] = [];
  if (!text) return results;

  DURATION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DURATION_RE.exec(text)) !== null) {
    const start = m.index;
    let end = start + m[0].length;
    const [, n1, , u1] = m;

    // Must start at a word boundary: not mid-number ("x12", "1.5" tail) or
    // mid-word ("extra minute" must not read as "a minute").
    if (start > 0 && /[\w/.]/.test(text[start - 1])) continue;
    // Recurring cues ("stir every 5 minutes") aren't a single countdown.
    if (/\bevery\s*$/i.test(text.slice(Math.max(0, start - 12), start))) continue;

    const first = parseNumber(n1);
    if (first == null) continue;
    let seconds = first * unitSeconds(u1);

    COMPOUND_RE.lastIndex = end;
    const tail = COMPOUND_RE.exec(text);
    if (tail) {
      const second = parseNumber(tail[1]);
      // Compound durations step down in unit ("1 hour 15 minutes").
      if (second != null && unitSeconds(tail[2]) < unitSeconds(u1)) {
        seconds += second * unitSeconds(tail[2]);
        end = COMPOUND_RE.lastIndex;
      }
    }
    RANGE_TAIL_RE.lastIndex = end;
    if (RANGE_TAIL_RE.exec(text)) end = RANGE_TAIL_RE.lastIndex;
    DURATION_RE.lastIndex = end;

    seconds = Math.round(seconds);
    if (seconds < MIN_SECONDS || seconds > MAX_SECONDS) continue;
    results.push({ start, end, text: text.slice(start, end), seconds });
  }
  return results;
};

/** Compact human label for a duration: "30 min", "1 hr 15 min", "45 sec". */
export const formatDurationLabel = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} hr`);
  if (m) parts.push(`${m} min`);
  if (s && !h) parts.push(`${s} sec`);
  return parts.join(' ') || '0 sec';
};

/** Countdown display: "4:05", "1:02:09". Negative values clamp to 0. */
export const formatCountdown = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};
